import { Navigate } from 'react-router-dom';

import { FeatureFlagGuard } from '@platform/client/feature-flags';
import type { FeatureConfig } from '@platform/client/features';

import { LegacyCapabilitiesGuard } from '@packages/components/capabilities/legacy-capabilities-guard';
import { OrganizationFlagsGuard } from '@packages/components/organization-flags-guard';
import { env } from '@packages/utils/env';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';

import i18n from './locales/i18n';
import AllRisksPage from './pages/all-risks/all-risks.page';
import VulnerabilitiesPage from './pages/vulnerabilities/vulnerabilities.page';

const riskManagementCapabilities = { canAccessManageRisk: true };

/**
 * TODO: Review pages to be able to use lazy loading and avoid page reloading.
 */
export default {
  routes: [
    {
      path: 'risk-management',
      element: (
        <FeatureFlagGuard featureFlag="RISKS_IN_UP_M1">
          <OrganizationFlagsGuard flags={{ risksInUpM1: true }}>
            <LegacyCapabilitiesGuard
              capabilities={riskManagementCapabilities}
            />
          </OrganizationFlagsGuard>
        </FeatureFlagGuard>
      ),
      children: [
        {
          path: 'risk-exposure-overview',
          lazy: () =>
            import(
              './pages/risk-exposure-overview/risk-exposure-overview.page'
            ),
        },
        {
          path: 'risks',
          lazy: () => import('./pages/risks/risks.page'),
          children: [
            {
              index: true,
              element: <Navigate to="all" replace />,
            },
            {
              path: 'all',
              element: <AllRisksPage />,
            },
            {
              path: 'vulnerabilities',
              element: <VulnerabilitiesPage />,
            },
          ],
        },
        {
          path: 'risks/:riskId',
          lazy: () => import('./pages/view-risk/view-risk.page'),
          children: [
            {
              index: true,
              element: <Navigate to="risk-details" replace />,
            },
            {
              path: 'risk-details',
              lazy: () => import('./pages/risk-details/risk-details.page'),
            },
            {
              path: 'risk-evidence',
              lazy: () => import('./pages/risk-evidence/risk-evidence.page'),
            },
          ],
        },
      ],
    },
  ],
  navigation: ({ legacyCapabilities, featureFlags, organization, orgId }) => {
    const hasManagedRiskAccess =
      legacyCapabilities &&
      hasAnyRequiredProperty(riskManagementCapabilities, legacyCapabilities);

    if (!hasManagedRiskAccess) return {};

    const canViewRisksInUpM1 =
      featureFlags?.RISKS_IN_UP_M1 && organization?.flags.risksInUpM1;

    if (canViewRisksInUpM1) {
      return {
        riskManagement: {
          riskExposureOverview: {
            title: i18n.t('riskManagement:overview.title'),
            segment: '/risk-management/risk-exposure-overview',
          },
          risks: {
            title: i18n.t('riskManagement:risks.title'),
            segment: '/risk-management/risks',
          },
          legacyRiskDashboard: {
            title: i18n.t('navMenu:legacyRiskDashboard'),
            externalUrl: `${env.VITE_MANAGED_RISK_BASE_PATH}?id=${orgId}`,
          },
          viewAllRisks: null,
          viewMyAssets: null,
          riskScoreOverview: null,
          externalVulnerabilityAssessment: null,
        },
      };
    }

    return {
      riskManagement: {
        viewAllRisks: {
          title: i18n.t('navMenu:viewAllRisks'),
          externalUrl: `${env.VITE_MANAGED_RISK_BASE_PATH}/risks?id=${orgId}`,
        },
        viewMyAssets: {
          title: i18n.t('navMenu:viewMyAssets'),
          externalUrl: `${env.VITE_MANAGED_RISK_BASE_PATH}/assets?id=${orgId}`,
        },
        riskScoreOverview: {
          title: i18n.t('navMenu:riskScoreOverview'),
          externalUrl: `${env.VITE_MANAGED_RISK_BASE_PATH}?id=${orgId}`,
        },
        externalVulnerabilityAssessment: {
          title: i18n.t('navMenu:externalVulnerabilityAssessment'),
          externalUrl: `${env.VITE_MANAGED_RISK_BASE_PATH}/reach.html?id=${orgId}`,
        },
        riskExposureOverview: null,
        risks: null,
        legacyRiskDashboard: null,
      },
    };
  },
} satisfies FeatureConfig;
